import { GameDocumentContext } from '../../../contexts/game-document';
import { useContext, useEffect, useState } from 'react';
import Toolbar from '../toolbar';
import {
  DeleteTitleAsync,
  GetResourceValue,
  UpdateGameDocState,
  UpdateResourceAsync,
  UpdateTitleAsync
} from '../../../utils/game-document';
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import {
  CheckboxChangeEvent,
  InputChangeEvent,
  TextAreaChangeEvent
} from '@progress/kendo-react-inputs';
import { FluidForm } from '../../../components/forms';
import { ResourceEntity, TitleEntity } from '../../../types/game-document/';
import { TitleEditor } from '../../../features/game-document/titles';
import cloneDeep from 'lodash.clonedeep';
import RequiredFields from '../../../types/required-fields';
import {
  ERR_DUPLICATE_NAME_VALUE,
  ERR_INPUT_REQUIRED
} from '../../../constants/text';
import { useGameDocumentResources } from '../../../hooks/use-game-document-resources';
import { ActionTopRow } from '../../../components/game-designer/action-top-row';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { useToggle } from 'usehooks-ts';
import { useBreadcrumb } from '../../../contexts/breadcrumb';

const Title = () => {
  const { titleId } = useParams();
  const [state, setState] = useContext(GameDocumentContext);
  const navigate = useNavigate();

  const [titleResource, setTitleResource] = useState<ResourceEntity>();
  const [summaryResource, setSummaryResource] = useState<ResourceEntity>();
  const [imageResource, setImageResource] = useState<ResourceEntity>();
  const [resources, setResources] = useState<ResourceEntity[]>(
    [] as ResourceEntity[]
  );
  const [requiredFields, setRequiredFields] = useState<
    RequiredFields<TitleEntity>[]
  >([
    { name: 'name', errorMessage: '' },
    { name: 'titleResId', errorMessage: '' }
  ]);
  const [showConfirmationDelete, setShowConfirmationDelete] = useToggle(false);

  const gameDocumentFiles = useGameDocumentResources();

  const getEntityById = (titleId: string) =>
    state.gameDocument?.assets.titles?.find((i) => i.id === titleId)!;

  const [entity, setEntity] = useState<TitleEntity>(() =>
    getEntityById(titleId!)
  );

  useEffect(() => {
    setEntity(getEntityById(titleId!));
  }, [state]);

  useEffect(() => {
    if (entity) {
      // Set the page title.
      document.title = `${GetResourceValue(state.gameDocument!, entity.titleResId ?? '')} - ${state.gameDocument?.name}`;
      const titleRes = state.gameDocument?.resources.find(
        (title) => title.id === entity.titleResId
      )! as ResourceEntity;
      const summaryRes = state.gameDocument?.resources.find(
        (title) => title.id === entity.summaryResId
      )! as ResourceEntity;
      const imageRes = state.gameDocument?.resources.find(
        (title) => title.id === entity.imageResId
      )! as ResourceEntity;
      setTitleResource(titleRes);
      setSummaryResource(summaryRes);
      setImageResource(imageRes);
      setResources([titleRes, summaryRes, imageRes]);
    }
  }, [state, entity]);

  // handle change when edit fields
  const handleInputChange = (event: InputChangeEvent) => {
    const { name, value } = event.target;
    if (name === 'titleResId' || name === 'summaryResId') {
      setResources(
        resources.map((item) =>
          item.id === entity[name]
            ? { ...item, value: value as string }
            : { ...item }
        )
      );
      const resource = resources.find((item) => item.id === entity[name])!;
      resource.value = value as string;
      UpdateResourceAsync(state.gameDocument!, resource?.id, resource).then(
        (updatedGameDocument) => {
          setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
        }
      );
    } else {
      setRequiredFields((prev) =>
        prev.map((title: RequiredFields<TitleEntity>) => {
          const copyTitle = cloneDeep({ ...title });
          if (title.name === (name as keyof TitleEntity)) {
            const duplicateValueIndex: number =
              state.gameDocument?.assets.titles
                ?.filter((item: TitleEntity) => item.id !== entity.id)
                .findIndex(
                  (item: TitleEntity) =>
                    item.name.toLowerCase() === (value as string)?.toLowerCase()
                ) ?? -1;
            if ((value as string) === '') {
              copyTitle.errorMessage = ERR_INPUT_REQUIRED;
            } else if (duplicateValueIndex !== -1) {
              copyTitle.errorMessage = ERR_DUPLICATE_NAME_VALUE;
            }
          }
          return copyTitle;
        })
      );
      setEntity((prev) => ({
        ...prev,
        [name!]: value
      }));
      UpdateTitleAsync(
        state.gameDocument!,
        titleId!,
        {
          ...entity,
          [name!]: value
        },
        false
      ).then((updatedGameDocument) => {
        setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
      });
    }
  };

  const handleImageSelected = (
    url: string,
    size: number,
    name?: string,
    category?: string
  ) => {
    gameDocumentFiles.updateResource(
      entity.imageResId!,
      url,
      size,
      name ?? '',
      category,
      (resource) => {
        // update the resources in state...
        // TODO: is this even needed? the game document state is updated which would trigger the effect on line 55...
        setResources((current) =>
          current.map((item) =>
            item.id === entity.imageResId ? resource : item
          )
        );
      }
    );
  };

  const onDeleteEntity = async (entityId: string) => {
    DeleteTitleAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
        navigate('../titles');
      }
    );
  };

  const handleTextAreaChange = (event: TextAreaChangeEvent) => {
    const { name, value } = event.target;
    setEntity({ ...entity, [name!]: value });
    UpdateTitleAsync(
      state.gameDocument!,
      titleId!,
      {
        ...entity,
        [name!]: value
      },
      false
    ).then((updatedGameDocument) => {
      setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
    });
  };

  const handleHideTitleChange = (event: CheckboxChangeEvent) => {
    const { name, value } = event.target;
    UpdateTitleAsync(
      state.gameDocument!,
      titleId!,
      {
        ...entity,
        [name!]: value
      },
      false
    ).then((updatedGameDocument) => {
      setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
    });
  };

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  const getTitleName = (titleId: string | undefined) => {
    if (titleId) {
      const title = state.gameDocument?.assets.titles?.find(
        (i) => i.id === titleId
      )!;
      if (title) {
        return GetResourceValue(state.gameDocument!, title.titleResId ?? '');
      }
    }
  };

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/titles/*', text: 'Title' },
      {
        path: '/designer/:gameId/assets/titles/:titleId/*',
        text: getTitleName(titleId)
      }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }
    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname, state.gameDocument]);

  return (
    <>
      <Toolbar
        title={GetResourceValue(state.gameDocument!, entity.titleResId ?? '')}
      />
      <ActionTopRow onDeleteClick={setShowConfirmationDelete} />
      <FluidForm>
        <TitleEditor
          editorMode={'full'}
          entity={entity}
          resources={resources}
          imageResourceVisibility={true}
          handleInputChange={handleInputChange}
          handleTextAreaChange={handleTextAreaChange}
          handleImageSelected={handleImageSelected}
          requiredFields={requiredFields}
          handleHideTitleChange={handleHideTitleChange}
        />
      </FluidForm>

      {showConfirmationDelete && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={(action) =>
            action === 'yes'
              ? onDeleteEntity(entity.id)
              : setShowConfirmationDelete()
          }
          onClose={() => setShowConfirmationDelete()}>
          Are you sure you want to remove the title?
        </YesNoDialog>
      )}
    </>
  );
};

export default Title;
